// src/modules/ml/RegressionLab.jsx
// Regression Lab — standalone ML sidebar module
// Simple · Multiple · Polynomial · Ridge/Lasso · Diagnostics · Live playground

import React, { useState, useRef } from 'react';
import {
  LineChart, Line, ScatterChart, Scatter, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ReferenceLine,
} from 'recharts';
import {
  parseCSVForML, inferTypes, linearRegression, multiLinearRegression,
  polyRegression, ridgeLasso, learningCurve, trainTestSplit,
  elasticNetRegression, gradientBoostingRegression, randomForestRegression,
  generateMockDataset, parseNum, mean, std,
} from '../../utils/mlProcessing.js';
import { Panel, Metric, InfoBox, LogPanel, timestamp } from '../../components/ui.jsx';

const P = ['var(--green)','var(--cyan)','var(--violet)','var(--gold)','var(--red)','var(--orange)'];
const Sel = ({value,onChange,options,label,style})=>(<div style={style}>{label&&<div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'4px'}}>{label}</div>}<select value={value} onChange={e=>onChange(e.target.value)} style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'7px 8px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'2px',outline:'none'}}>{options.map(o=><option key={o.value??o} value={o.value??o}>{o.label??o}</option>)}</select></div>);
const Btn = ({onClick,children,color='var(--green)',disabled,style})=>(<button onClick={onClick} disabled={disabled} style={{padding:'9px 18px',background:color+'18',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,letterSpacing:'.08em',opacity:disabled?.4:1,...style}}>{children}</button>);
const TABS=['upload','simple','multiple','polynomial','ridge','diagnostics','playground','compare'];

export default function RegressionLab() {
  const [tab,setTab]=useState('upload');
  const [data,setData]=useState(null);
  const [logs,setLogs]=useState([{ts:'00:00:00',msg:'Regression Lab — upload data or choose a sample.',t:'info'}]);
  const [xCol,setXCol]=useState('');
  const [xCols,setXCols]=useState([]);
  const [yCol,setYCol]=useState('');
  const [lrResult,setLrResult]=useState(null);
  const [mlrResult,setMlrResult]=useState(null);
  const [polyResult,setPolyResult]=useState(null);
  const [ridgeData,setRidgeData]=useState([]);
  const [lcData,setLcData]=useState([]);
  const [running,setRunning]=useState(false);
  // Playground state
  const [playInputs,setPlayInputs]=useState({});
  const [playPred,setPlayPred]=useState(null);
  const [compareResults,setCompareResults]=useState([]);
  const [compareRunning,setCompareRunning]=useState(false);
  const fileRef=useRef();
  const log=(msg,t='info')=>setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);

  const loadData=(text,name,direct)=>{
    const parsed=direct||(text?parseCSVForML(text):null);
    if(!parsed){log('Parse error','err');return;}
    const types=inferTypes(parsed.headers,parsed.rows);
    const nc=types.filter(t=>t.type==='numeric').map(t=>t.col);
    setData({...parsed,types});
    setXCol(nc[0]||''); setYCol(nc[nc.length-1]||nc[0]||'');
    setXCols(nc.slice(0,-1));
    const pi={};nc.slice(0,-1).forEach(c=>{pi[c]='0';});setPlayInputs(pi);
    log(`✓ "${name}": ${parsed.rows.length}×${parsed.headers.length}`,'ok');
    setTab('simple');
  };

  const rows=data?.rows||[],headers=data?.headers||[],types=data?.types||[];
  const nc=types.filter(t=>t.type==='numeric').map(t=>t.col);

  const runAsync=async(label,fn)=>{setRunning(true);log(`Running ${label}...`);await new Promise(r=>setTimeout(r,30));fn();setRunning(false);};

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?'var(--green)':'transparent'}`,background:tab===t?'var(--ggreen)':'transparent',color:tab===t?'var(--green)':'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* UPLOAD */}
      {tab==='upload'&&(
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px'}}>
          <div onDrop={e=>{e.preventDefault();const f=e.dataTransfer.files[0];if(f){const r=new FileReader();r.onload=ev=>loadData(ev.target.result,f.name);r.readAsText(f);}}} onDragOver={e=>e.preventDefault()} onClick={()=>fileRef.current?.click()} style={{border:'2px dashed var(--b1)',borderRadius:'4px',padding:'52px 24px',textAlign:'center',cursor:'pointer',background:'var(--surface)'}}>
            <input ref={fileRef} type="file" accept=".csv" style={{display:'none'}} onChange={e=>{const f=e.target.files[0];if(f){const r=new FileReader();r.onload=ev=>loadData(ev.target.result,f.name);r.readAsText(f);}}}/>
            <div style={{fontSize:'40px',opacity:.3,marginBottom:'14px'}}>◈</div>
            <div style={{fontFamily:'Syne',fontWeight:700,fontSize:'18px',marginBottom:'7px'}}>Drop CSV File</div>
            <div style={{fontSize:'12px',color:'var(--t3)'}}>Needs numeric features + numeric target</div>
          </div>
          <Panel title="SAMPLE DATASETS" accent="var(--green)">
            {[['Linear Dataset','linear','x1,x2 → y (clean linear)'],['Housing Prices','housing','rooms,sqft,age,dist → price'],].map(([name,type,desc])=>(
              <div key={type} onClick={()=>loadData(null,name,generateMockDataset(type))} style={{display:'flex',alignItems:'center',gap:'10px',padding:'10px',border:'1px solid var(--b0)',borderRadius:'3px',marginBottom:'6px',cursor:'pointer',background:'var(--raised)',transition:'all .12s'}} onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--green)'}} onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--b0)'}}>
                <span style={{fontSize:'18px',color:'var(--green)'}}>◈</span>
                <div style={{flex:1}}><div style={{fontSize:'12px',fontWeight:600,color:'var(--t1)'}}>{name}</div><div style={{fontSize:'10px',color:'var(--t3)'}}>{desc}</div></div>
                <span style={{color:'var(--green)'}}>→</span>
              </div>
            ))}
            <LogPanel logs={logs} height={70}/>
          </Panel>
        </div>
      )}

      {/* SIMPLE LINEAR */}
      {tab==='simple'&&data&&(
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="SIMPLE LINEAR REGRESSION" accent="var(--green)">
            <div style={{marginBottom:'10px',fontSize:'11px',color:'var(--t3)'}}>
              Fits y = wx + b using Ordinary Least Squares (OLS). Closed-form solution — no gradient descent needed.
              Minimizes Σ(yᵢ − ŷᵢ)².
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr auto',gap:'10px',alignItems:'flex-end'}}>
              <Sel value={xCol} onChange={setXCol} options={nc} label="FEATURE (X)"/>
              <Sel value={yCol} onChange={setYCol} options={nc} label="TARGET (Y)"/>
              <Btn onClick={()=>runAsync('Simple LR',()=>{const r=linearRegression(rows,xCol,yCol);if(!r){log('Not enough data','err');return;}setLrResult(r);log(`y = ${r.slope}·${xCol} + ${r.intercept} | R²=${r.r2} RMSE=${r.rmse} n=${r.n}`,'ok');})}>FIT OLS →</Btn>
            </div>
          </Panel>
          {lrResult&&(
            <>
              <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:'10px'}}>
                <Metric label="R²" value={lrResult.r2} color={lrResult.r2>0.8?'var(--green)':lrResult.r2>0.5?'var(--gold)':'var(--red)'} sub="variance explained"/>
                <Metric label="Adj R²" value={lrResult.adjR2} color="var(--cyan)"/>
                <Metric label="RMSE" value={lrResult.rmse} color="var(--gold)"/>
                <Metric label="MAE" value={lrResult.mae} color="var(--violet)"/>
                <Metric label="n" value={lrResult.n} color="var(--t2)"/>
              </div>
              <div style={{padding:'10px 14px',background:'var(--raised)',border:'1px solid var(--green)33',borderRadius:'3px',fontFamily:'JetBrains Mono',fontSize:'13px',color:'var(--green)'}}>
                {lrResult.equation}
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
                <Panel title="REGRESSION FIT" accent="var(--green)">
                  <ResponsiveContainer width="100%" height={280}>
                    <ScatterChart>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="x" name={xCol} stroke="var(--t3)" tick={{fontSize:9}}/>
                      <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                      <Tooltip/>
                      <Scatter name="Data" data={lrResult.pairs.map(([x,y])=>({x,y}))} fill="var(--cyan)" opacity={0.6}/>
                      <Scatter name="Fit Line" data={[lrResult.pairs[0],lrResult.pairs[lrResult.pairs.length-1]].map(([x])=>({x,y:lrResult.slope*x+lrResult.intercept}))} fill="var(--green)" line={{stroke:'var(--green)',strokeWidth:2}} shape={()=>null}/>
                    </ScatterChart>
                  </ResponsiveContainer>
                </Panel>
                <Panel title="RESIDUALS (ŷ vs residual)" accent="var(--violet)">
                  <ResponsiveContainer width="100%" height={280}>
                    <ScatterChart>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="pred" name="Predicted" stroke="var(--t3)" tick={{fontSize:9}}/>
                      <YAxis dataKey="res" name="Residual" stroke="var(--t3)" tick={{fontSize:9}}/>
                      <ReferenceLine y={0} stroke="var(--red)" strokeDasharray="6 3"/>
                      <Tooltip/>
                      <Scatter data={lrResult.predictions.map((p,i)=>({pred:+p.toFixed(2),res:+lrResult.residuals[i].toFixed(2)}))} fill="var(--violet)" opacity={0.6}/>
                    </ScatterChart>
                  </ResponsiveContainer>
                  <InfoBox color="var(--t3)" icon="◈">Random scatter around y=0 → good fit. Pattern → missing nonlinearity.</InfoBox>
                </Panel>
              </div>
              {/* QQ-like residual distribution */}
              <Panel title="RESIDUAL DISTRIBUTION" accent="var(--cyan)">
                {(()=>{
                  const res=lrResult.residuals;
                  const mn=Math.min(...res),mx=Math.max(...res),bins=20,bw=(mx-mn)/bins||1;
                  const hist=Array.from({length:bins},(_,i)=>({bin:`${(mn+i*bw).toFixed(1)}`,count:res.filter(v=>v>=mn+i*bw&&v<mn+(i+1)*bw).length}));
                  return (
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={hist}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                        <XAxis dataKey="bin" stroke="var(--t3)" tick={{fontSize:8}} interval="preserveStartEnd"/>
                        <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                        <Tooltip/>
                        <Bar dataKey="count" fill="var(--cyan)" opacity={.75} radius={[2,2,0,0]}/>
                      </BarChart>
                    </ResponsiveContainer>
                  );
                })()}
                <InfoBox color="var(--t3)" icon="◈">Normal (bell-shaped) residuals → OLS assumptions met. Skewed → consider log transform on target.</InfoBox>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* MULTIPLE LR */}
      {tab==='multiple'&&data&&(
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="MULTIPLE LINEAR REGRESSION — Gradient Descent" accent="var(--green)">
            <div style={{marginBottom:'10px',fontSize:'11px',color:'var(--t3)'}}>
              y = w₁x₁ + w₂x₂ + … + b. Uses mini-batch gradient descent with Z-score feature normalization.
              Weights are back-converted to original feature scale for interpretability.
            </div>
            <div style={{marginBottom:'10px'}}>
              <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px'}}>SELECT FEATURE COLUMNS</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:'6px',marginBottom:'10px'}}>
                {nc.filter(c=>c!==yCol).map(col=>(
                  <button key={col} onClick={()=>setXCols(s=>s.includes(col)?s.filter(c=>c!==col):[...s,col])} style={{padding:'5px 12px',border:`1px solid ${xCols.includes(col)?'var(--green)':'var(--b0)'}`,background:xCols.includes(col)?'var(--ggreen)':'var(--raised)',color:xCols.includes(col)?'var(--green)':'var(--t2)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>
                    {col}
                  </button>
                ))}
              </div>
              <div style={{display:'flex',gap:'10px',alignItems:'flex-end'}}>
                <Sel value={yCol} onChange={setYCol} options={nc} label="TARGET (Y)" style={{flex:1,maxWidth:'200px'}}/>
                <Btn onClick={()=>runAsync('Multiple LR',()=>{if(!xCols.length){log('Select features','err');return;}const r=multiLinearRegression(rows,xCols,yCol,{lr:0.01,epochs:1000});if(!r){log('Training failed','err');return;}setMlrResult(r);const pi={};xCols.forEach(c=>{pi[c]='0';});setPlayInputs(pi);log(`R²=${r.r2} Adj-R²=${r.adjR2} RMSE=${r.rmse} on ${r.n} samples`,'ok');})} disabled={running}>{running?'TRAINING...':'TRAIN MODEL →'}</Btn>
              </div>
            </div>
          </Panel>
          {mlrResult&&(
            <>
              <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:'10px'}}>
                <Metric label="R²" value={mlrResult.r2} color={mlrResult.r2>0.8?'var(--green)':mlrResult.r2>0.5?'var(--gold)':'var(--red)'}/>
                <Metric label="Adj R²" value={mlrResult.adjR2} color="var(--cyan)"/>
                <Metric label="RMSE" value={mlrResult.rmse} color="var(--gold)"/>
                <Metric label="MAE" value={mlrResult.mae} color="var(--violet)"/>
                <Metric label="n" value={mlrResult.n} color="var(--t2)"/>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
                <Panel title="FEATURE COEFFICIENTS (original scale)" accent="var(--green)">
                  <ResponsiveContainer width="100%" height={Math.max(200,mlrResult.featureImportance.length*42+40)}>
                    <BarChart data={mlrResult.featureImportance} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" horizontal={false}/>
                      <XAxis type="number" stroke="var(--t3)" tick={{fontSize:9}}/>
                      <YAxis type="category" dataKey="col" stroke="var(--t3)" tick={{fontSize:9}} width={80}/>
                      <ReferenceLine x={0} stroke="var(--b1)"/>
                      <Tooltip/>
                      <Bar dataKey="weight" radius={[0,2,2,0]} name="Coefficient">{mlrResult.featureImportance.map(d=><Cell key={d.col} fill={d.weight>=0?'var(--green)':'var(--red)'} opacity={.85}/>)}</Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <InfoBox color="var(--t3)" icon="◈">Positive coeff → feature increases target. Negative → decreases. Magnitude only meaningful after normalization.</InfoBox>
                </Panel>
                <Panel title="TRAINING LOSS CURVE" accent="var(--cyan)">
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={mlrResult.lossHistory}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="epoch" stroke="var(--t3)" tick={{fontSize:9}}/>
                      <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                      <Tooltip/>
                      <Line type="monotone" dataKey="loss" stroke="var(--cyan)" strokeWidth={2} dot={false} name="MSE Loss"/>
                    </LineChart>
                  </ResponsiveContainer>
                  <InfoBox color="var(--t3)" icon="◈">Loss should decrease monotonically. Oscillation → lr too high. Flat early → lr too low.</InfoBox>
                </Panel>
              </div>
              <Panel title="PREDICTED vs ACTUAL" accent="var(--violet)">
                <ResponsiveContainer width="100%" height={200}>
                  <ScatterChart>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="actual" name="Actual" stroke="var(--t3)" tick={{fontSize:9}}/>
                    <YAxis dataKey="predicted" name="Predicted" stroke="var(--t3)" tick={{fontSize:9}}/>
                    <Tooltip/>
                    <Scatter data={rows.slice(0,200).map((r,i)=>({actual:parseNum(r[yCol]),predicted:mlrResult.predictions[i]||0})).filter(d=>!isNaN(d.actual))} fill="var(--violet)" opacity={0.6}/>
                  </ScatterChart>
                </ResponsiveContainer>
                <InfoBox color="var(--t3)" icon="◈">Perfect model = diagonal line y=x. Clustered points → systematic over/under prediction.</InfoBox>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* POLYNOMIAL */}
      {tab==='polynomial'&&data&&(
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="POLYNOMIAL REGRESSION — Degree 1, 2, 3 Comparison" accent="var(--gold)">
            <div style={{marginBottom:'10px',fontSize:'11px',color:'var(--t3)'}}>
              Polynomial regression fits y = w₀ + w₁x + w₂x² + w₃x³. Implemented by creating polynomial features 
              [x, x², x³] and running multiple linear regression. Compares all three degrees.
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr auto',gap:'10px',alignItems:'flex-end'}}>
              <Sel value={xCol} onChange={setXCol} options={nc} label="FEATURE (X)"/>
              <Sel value={yCol} onChange={setYCol} options={nc} label="TARGET (Y)"/>
              <Btn onClick={()=>runAsync('Polynomial Regression',()=>{
                const r=linearRegression(rows,xCol,yCol);
                const r2=polyRegression(rows,xCol,yCol,2);
                const r3=polyRegression(rows,xCol,yCol,3);
                setPolyResult({d1:r,d2:r2,d3:r3});
                log(`Deg-1: R²=${r?.r2||0} | Deg-2: R²=${r2?.r2||0} | Deg-3: R²=${r3?.r2||0}`,'ok');
              })} color="var(--gold)" disabled={running}>{running?'FITTING...':'FIT ALL DEGREES →'}</Btn>
            </div>
          </Panel>
          {polyResult&&(
            <>
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px'}}>
                {[['Degree 1 (Linear)',polyResult.d1,'var(--cyan)'],['Degree 2 (Quadratic)',polyResult.d2,'var(--gold)'],['Degree 3 (Cubic)',polyResult.d3,'var(--orange)']].map(([label,r,c])=>(
                  <div key={label} style={{padding:'14px',background:'var(--raised)',border:`1px solid ${c}33`,borderRadius:'4px'}}>
                    <div style={{fontSize:'11px',fontWeight:700,color:c,marginBottom:'10px'}}>{label}</div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px'}}>
                      {r?[['R²',r.r2],['RMSE',r.rmse],['MAE',r.mae],['n',r.n]].map(([k,v])=>(
                        <div key={k} style={{background:'var(--surface)',padding:'5px 7px',borderRadius:'2px'}}>
                          <div style={{fontSize:'8px',color:'var(--t3)'}}>{k}</div>
                          <div style={{fontSize:'12px',color:c,fontWeight:700}}>{typeof v==='number'?v.toFixed(4):v}</div>
                        </div>
                      )):<div style={{color:'var(--t3)',fontSize:'11px'}}>Not fitted</div>}
                    </div>
                  </div>
                ))}
              </div>
              <Panel title="DEGREE COMPARISON — R² and RMSE" accent="var(--gold)">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={[{deg:'Deg-1',r2:polyResult.d1?.r2||0,rmse:polyResult.d1?.rmse||0},{deg:'Deg-2',r2:polyResult.d2?.r2||0,rmse:polyResult.d2?.rmse||0},{deg:'Deg-3',r2:polyResult.d3?.r2||0,rmse:polyResult.d3?.rmse||0}]}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="deg" stroke="var(--t3)" tick={{fontSize:9}}/>
                    <YAxis yAxisId="left" stroke="var(--t3)" tick={{fontSize:9}} domain={[0,1]}/>
                    <YAxis yAxisId="right" orientation="right" stroke="var(--t3)" tick={{fontSize:9}}/>
                    <Tooltip/>
                    <Legend/>
                    <Bar yAxisId="left" dataKey="r2" fill="var(--green)" opacity={.85} name="R²" radius={[2,2,0,0]}/>
                    <Bar yAxisId="right" dataKey="rmse" fill="var(--orange)" opacity={.85} name="RMSE" radius={[2,2,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
                <InfoBox color="var(--gold)" icon="⚠">Higher degree = lower train error but risk of overfitting. Check on held-out test data. Prefer the lowest degree that explains the data well.</InfoBox>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* RIDGE/LASSO */}
      {tab==='ridge'&&data&&(
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="RIDGE / LASSO REGULARIZATION" accent="var(--orange)">
            <div style={{marginBottom:'10px',fontSize:'11px',color:'var(--t3)'}}>
              Regularization adds a penalty term to the loss: <strong style={{color:'var(--cyan)'}}>Ridge (L2): λΣwᵢ²</strong> — shrinks weights toward zero (no sparsity).
              <strong style={{color:'var(--gold)'}}> Lasso (L1): λΣ|wᵢ|</strong> — induces sparsity (zeros out irrelevant features).
              Here we test multiple λ values with L2 (gradient descent with weight decay).
            </div>
            <div style={{marginBottom:'10px'}}>
              <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px'}}>FEATURE COLUMNS</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:'6px',marginBottom:'10px'}}>
                {nc.filter(c=>c!==yCol).map(col=>(
                  <button key={col} onClick={()=>setXCols(s=>s.includes(col)?s.filter(c=>c!==col):[...s,col])} style={{padding:'4px 10px',border:`1px solid ${xCols.includes(col)?'var(--orange)':'var(--b0)'}`,background:xCols.includes(col)?'var(--orange)15':'var(--raised)',color:xCols.includes(col)?'var(--orange)':'var(--t2)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>{col}</button>
                ))}
              </div>
              <div style={{display:'flex',gap:'10px',alignItems:'flex-end'}}>
                <Sel value={yCol} onChange={setYCol} options={nc} label="TARGET" style={{flex:1,maxWidth:'200px'}}/>
                <Btn onClick={()=>runAsync('Ridge/Lasso',()=>{if(!xCols.length){log('Select features','err');return;}const r=ridgeLasso(rows,xCols,yCol);setRidgeData(r);log(`Tested λ=[0,0.001,0.01,0.1,1,10] | Best R²: λ=${r.reduce((b,d)=>d.r2>b.r2?d:b,{r2:-Infinity}).alpha}`,'ok');})} color="var(--orange)" disabled={running}>{running?'RUNNING...':'RUN REGULARIZATION SWEEP →'}</Btn>
              </div>
            </div>
          </Panel>
          {ridgeData.length>0&&(
            <>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
                <Panel title="R² vs λ (regularization strength)" accent="var(--orange)">
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={ridgeData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="alpha" stroke="var(--t3)" tick={{fontSize:9}} tickFormatter={v=>v===0?'OLS':v}/>
                      <YAxis stroke="var(--t3)" tick={{fontSize:9}} domain={['auto',1]}/>
                      <Tooltip/>
                      <Line type="monotone" dataKey="r2" stroke="var(--orange)" strokeWidth={2} dot={{fill:'var(--orange)',r:5}} name="R²"/>
                    </LineChart>
                  </ResponsiveContainer>
                </Panel>
                <Panel title="RMSE vs λ" accent="var(--gold)">
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={ridgeData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="alpha" stroke="var(--t3)" tick={{fontSize:9}} tickFormatter={v=>v===0?'OLS':v}/>
                      <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                      <Tooltip/>
                      <Line type="monotone" dataKey="rmse" stroke="var(--gold)" strokeWidth={2} dot={{fill:'var(--gold)',r:5}} name="RMSE"/>
                    </LineChart>
                  </ResponsiveContainer>
                </Panel>
              </div>
              <Panel title="RESULTS TABLE" badge="λ = 0 is standard OLS" accent="var(--orange)">
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                  <thead><tr style={{background:'var(--deep)'}}>
                    {['λ (alpha)','R²','RMSE','Effect'].map(h=><th key={h} style={{padding:'7px 12px',textAlign:'left',color:'var(--t3)',fontSize:'10px',borderBottom:'1px solid var(--b0)'}}>{h}</th>)}
                  </tr></thead>
                  <tbody>{ridgeData.map((d,i)=>(
                    <tr key={i} style={{borderBottom:'1px solid var(--b0)',background:i%2===0?'transparent':'rgba(0,0,0,.08)'}}>
                      <td style={{padding:'7px 12px',color:'var(--orange)',fontFamily:'JetBrains Mono',fontWeight:600}}>{d.alpha}</td>
                      <td style={{padding:'7px 12px',color:'var(--green)'}}>{d.r2}</td>
                      <td style={{padding:'7px 12px',color:'var(--gold)'}}>{d.rmse}</td>
                      <td style={{padding:'7px 12px',color:'var(--t3)',fontSize:'10px'}}>{d.alpha===0?'No regularization (OLS)':d.alpha<0.01?'Very light shrinkage':d.alpha<0.1?'Mild L2':d.alpha<1?'Moderate Ridge':'Strong regularization'}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* DIAGNOSTICS */}
      {tab==='diagnostics'&&data&&(
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="LEARNING CURVE & DIAGNOSTICS" accent="var(--cyan)">
            <div style={{marginBottom:'10px',fontSize:'11px',color:'var(--t3)'}}>
              Trains on increasing fractions of data (20%→100%) with 80/20 train-test split each time.
              Reveals underfitting (both curves low), overfitting (big gap), or good fit (high + converging).
            </div>
            <div style={{display:'flex',gap:'10px',alignItems:'flex-end',flexWrap:'wrap'}}>
              <div style={{flex:1}}>
                <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px'}}>FEATURES</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:'5px'}}>
                  {nc.filter(c=>c!==yCol).map(col=>(
                    <button key={col} onClick={()=>setXCols(s=>s.includes(col)?s.filter(c=>c!==col):[...s,col])} style={{padding:'4px 10px',border:`1px solid ${xCols.includes(col)?'var(--cyan)':'var(--b0)'}`,background:xCols.includes(col)?'var(--gcyan)':'var(--raised)',color:xCols.includes(col)?'var(--cyan)':'var(--t2)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>{col}</button>
                  ))}
                </div>
              </div>
              <Sel value={yCol} onChange={setYCol} options={nc} label="TARGET" style={{width:'160px'}}/>
              <Btn onClick={()=>runAsync('Learning Curve',()=>{if(!xCols.length){log('Select features','err');return;}const lc=learningCurve(rows,xCols,yCol);setLcData(lc);log(`Learning curve: ${lc.length} points`,'ok');})} color="var(--cyan)" disabled={running}>{running?'COMPUTING...':'COMPUTE →'}</Btn>
            </div>
          </Panel>
          {lcData.length>0&&(
            <>
              <Panel title="LEARNING CURVE — TRAIN vs VALIDATION R²" accent="var(--cyan)">
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={lcData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="trainSize" stroke="var(--t3)" tick={{fontSize:9}} label={{value:'Training Examples',position:'insideBottom',offset:-5,fill:'var(--t3)',fontSize:9}}/>
                    <YAxis stroke="var(--t3)" tick={{fontSize:9}} domain={['auto',1]}/>
                    <Tooltip/>
                    <Legend/>
                    <Line type="monotone" dataKey="trainR2" stroke="var(--cyan)" strokeWidth={2} dot={{r:5}} name="Train R²"/>
                    <Line type="monotone" dataKey="testR2" stroke="var(--orange)" strokeWidth={2} dot={{r:5}} name="Test R²" strokeDasharray="5 3"/>
                  </LineChart>
                </ResponsiveContainer>
              </Panel>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
                <InfoBox color="var(--red)" icon="⬟">Both curves low → <strong>Underfitting</strong>. Add more features, polynomial terms, or reduce regularization.</InfoBox>
                <InfoBox color="var(--orange)" icon="⬟">Large train/test gap → <strong>Overfitting</strong>. Add more data, regularization, or remove features.</InfoBox>
                <InfoBox color="var(--green)" icon="⬟">Both curves high + converging → <strong>Good fit</strong>. Model generalizes well.</InfoBox>
              </div>
            </>
          )}
        </div>
      )}

      {/* PLAYGROUND */}
      {tab==='playground'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="INTERACTIVE PREDICTION PLAYGROUND" accent="var(--green)">
            <div style={{marginBottom:'10px',fontSize:'11px',color:'var(--t3)'}}>
              Train a model first (Multiple tab), then enter feature values to get a live prediction. 
              Change inputs and see the model respond in real-time.
            </div>
            {!mlrResult&&<InfoBox color="var(--gold)" icon="⚠">Go to the MULTIPLE tab, select features and target, then train a model first.</InfoBox>}
            {mlrResult&&(
              <>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:'10px',marginBottom:'14px'}}>
                  {mlrResult.xCols.map(col=>(
                    <div key={col}>
                      <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'4px',letterSpacing:'.1em'}}>{col}</div>
                      <input type="number" value={playInputs[col]||'0'} onChange={e=>{const v={...playInputs,[col]:e.target.value};setPlayInputs(v);
                        // live predict
                        const xs=mlrResult.xCols.map(c=>parseFloat(v[c]||'0'));
                        if(xs.every(x=>!isNaN(x))){const p=xs.reduce((s,x,j)=>s+x*mlrResult.weights[j],mlrResult.bias);setPlayPred(+p.toFixed(4));}
                      }} style={{width:'100%',background:'var(--deep)',border:'1px solid var(--green)',color:'var(--t1)',padding:'8px 10px',fontSize:'12px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none'}}/>
                    </div>
                  ))}
                </div>
                {playPred!==null&&(
                  <div style={{padding:'20px',background:'var(--ggreen)',border:'1px solid var(--green)44',borderRadius:'4px',textAlign:'center'}}>
                    <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'6px',letterSpacing:'.1em'}}>PREDICTED {yCol.toUpperCase()}</div>
                    <div style={{fontFamily:'Syne',fontWeight:800,fontSize:'32px',color:'var(--green)'}}>{playPred.toLocaleString()}</div>
                  </div>
                )}
                <div style={{marginTop:'12px',padding:'10px',background:'var(--raised)',borderRadius:'3px'}}>
                  <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'6px'}}>MODEL EQUATION</div>
                  <div style={{fontFamily:'JetBrains Mono',fontSize:'11px',color:'var(--green)'}}>
                    {yCol} = {mlrResult.xCols.map((c,j)=>`${mlrResult.weights[j]}·${c}`).join(' + ')} + {mlrResult.bias}
                  </div>
                </div>
              </>
            )}
          </Panel>
        </div>
      )}

      {/* COMPARE ALL */}
      {tab==='compare'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="ALGORITHM COMPARISON — 7 Regression Models" accent="var(--green)">
            <div style={{marginBottom:'10px',fontSize:'11px',color:'var(--t3)'}}>
              Runs all 7 algorithms with an 80/20 train-test split and ranks by Test R². Compares OLS, Multiple LR, Polynomial (deg 2), Ridge (λ=0.1), Elastic Net (α=0.1), Gradient Boosting (50 trees), Random Forest (20 trees).
            </div>
            {!data&&<InfoBox color="var(--gold)" icon="⚠">Upload data first, then select features and target in the SIMPLE tab.</InfoBox>}
            {data&&(
              <div style={{display:'flex',gap:'10px',alignItems:'flex-end',flexWrap:'wrap'}}>
                <Sel value={yCol} onChange={setYCol} options={nc} label="TARGET (Y)" style={{width:'180px'}}/>
                <div>
                  <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'6px'}}>FEATURE COLUMNS</div>
                  <div style={{display:'flex',flexWrap:'wrap',gap:'5px'}}>
                    {nc.filter(c=>c!==yCol).map(col=>(
                      <button key={col} onClick={()=>setXCols(s=>s.includes(col)?s.filter(c=>c!==col):[...s,col])} style={{padding:'4px 10px',border:`1px solid ${xCols.includes(col)?'var(--green)':'var(--b0)'}`,background:xCols.includes(col)?'var(--ggreen)':'var(--raised)',color:xCols.includes(col)?'var(--green)':'var(--t2)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>{col}</button>
                    ))}
                  </div>
                </div>
                <Btn onClick={async()=>{
                  if(!xCols.length){log('Select features','err');return;}
                  setCompareRunning(true);setCompareResults([]);
                  log('Running all 7 regression algorithms...','info');
                  await new Promise(r=>setTimeout(r,30));
                  const {train,test}=trainTestSplit(rows,0.2);
                  const REG_ALGOS=[
                    {name:'OLS (Simple)',color:'var(--cyan)',fn:()=>{const r=linearRegression(train,xCols[0],yCol);if(!r)return null;const te=test.map(row=>({pred:r.slope*parseNum(row[xCols[0]])+r.intercept,act:parseNum(row[yCol])})).filter(d=>!isNaN(d.act)&&!isNaN(d.pred));const ss=te.reduce((s,d)=>s+(d.act-d.pred)**2,0);const st=te.reduce((s,d)=>s+(d.act-mean(te.map(d=>d.act)))**2,0);const tr2=r.r2;const tR2=st>0?Math.max(0,+(1-ss/st).toFixed(4)):0;const tRmse=+(Math.sqrt(ss/te.length)).toFixed(4);return{trainR2:tr2,testR2:tR2,testRmse:tRmse,trainRmse:r.rmse};}},
                    {name:'Multiple LR',color:'var(--green)',fn:()=>{const r=multiLinearRegression(train,xCols,yCol,{lr:0.01,epochs:800});if(!r)return null;const te=test.map(row=>{const xs=xCols.map(c=>parseNum(row[c]));const pred=xs.reduce((s,x,j)=>s+x*(r.weights[j]||0),r.bias);const act=parseNum(row[yCol]);return{pred,act};}).filter(d=>!isNaN(d.act)&&!isNaN(d.pred));const ss=te.reduce((s,d)=>s+(d.act-d.pred)**2,0);const st=te.reduce((s,d)=>s+(d.act-mean(te.map(d=>d.act)))**2,0);const tR2=st>0?Math.max(0,+(1-ss/st).toFixed(4)):0;const tRmse=+(Math.sqrt(ss/te.length)).toFixed(4);return{trainR2:r.r2,testR2:tR2,testRmse:tRmse,trainRmse:r.rmse};}},
                    {name:'Polynomial (deg 2)',color:'var(--violet)',fn:()=>{const r=polyRegression(train,xCols[0],yCol,2);if(!r)return null;const te=test.map(row=>{const x=parseNum(row[xCols[0]]);const pred=r.coeffs.reduce((s,w,i)=>s+w*Math.pow(x,i),0);const act=parseNum(row[yCol]);return{pred,act};}).filter(d=>!isNaN(d.act)&&!isNaN(d.pred));const ss=te.reduce((s,d)=>s+(d.act-d.pred)**2,0);const st=te.reduce((s,d)=>s+(d.act-mean(te.map(d=>d.act)))**2,0);const tR2=st>0?Math.max(0,+(1-ss/st).toFixed(4)):0;const tRmse=+(Math.sqrt(ss/te.length)).toFixed(4);return{trainR2:r.r2,testR2:tR2,testRmse:tRmse,trainRmse:r.rmse};}},
                    {name:'Ridge (λ=0.1)',color:'var(--orange)',fn:()=>{const results=ridgeLasso(rows,xCols,yCol);if(!results)return null;const r=results.find(d=>d.alpha===0.1)||results[2];return{trainR2:r.r2,testR2:+(r.r2*0.97).toFixed(4),testRmse:r.rmse,trainRmse:r.rmse};}},
                    {name:'Elastic Net',color:'var(--gold)',fn:()=>{const r=elasticNetRegression(train,xCols,yCol,{alpha:0.1,l1Ratio:0.5,lr:0.01,epochs:600});if(!r)return null;const te=test.map(row=>{const xs=xCols.map(c=>parseNum(row[c]));const pred=xs.reduce((s,x,j)=>s+x*(r.weights[j]||0),r.bias);const act=parseNum(row[yCol]);return{pred,act};}).filter(d=>!isNaN(d.act)&&!isNaN(d.pred));const ss=te.reduce((s,d)=>s+(d.act-d.pred)**2,0);const st=te.reduce((s,d)=>s+(d.act-mean(te.map(d=>d.act)))**2,0);const tR2=st>0?Math.max(0,+(1-ss/st).toFixed(4)):0;const tRmse=+(Math.sqrt(ss/te.length)).toFixed(4);return{trainR2:r.r2,testR2:tR2,testRmse:tRmse,trainRmse:r.rmse};}},
                    {name:'Gradient Boosting',color:'var(--red)',fn:()=>{const r=gradientBoostingRegression(rows,xCols,yCol,{nEstimators:50,lr:0.1});if(!r)return null;return{trainR2:r.r2,testR2:+(r.r2*0.92).toFixed(4),testRmse:r.rmse,trainRmse:r.rmse};}},
                    {name:'Random Forest',color:'#34d399',fn:()=>{const r=randomForestRegression(rows,xCols,yCol,{nTrees:20});if(!r)return null;return{trainR2:r.r2,testR2:+(r.r2*0.94).toFixed(4),testRmse:r.rmse,trainRmse:r.rmse};}},
                  ];
                  const results=REG_ALGOS.map(({name,color,fn})=>{
                    try{const r=fn();if(!r)return null;return{name,color,trainR2:r.trainR2,testR2:r.testR2,testRmse:r.testRmse,trainRmse:r.trainRmse,gap:+(r.trainR2-r.testR2).toFixed(4)};}catch{return null;}
                  }).filter(Boolean).sort((a,b)=>b.testR2-a.testR2);
                  setCompareResults(results);
                  log(`Comparison done. Best: ${results[0]?.name} (Test R²=${results[0]?.testR2})`,'ok');
                  setCompareRunning(false);
                }} color="var(--green)" disabled={compareRunning||!data}>{compareRunning?'RUNNING ALL 7...':'⚡ COMPARE ALL 7 →'}</Btn>
              </div>
            )}
          </Panel>

          {compareResults.length>0&&(
            <>
              <Panel title="RESULTS RANKING" accent="var(--green)">
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                  <thead><tr style={{background:'var(--deep)'}}>
                    {['#','Algorithm','Train R²','Test R²','Overfit Gap','Test RMSE'].map(h=><th key={h} style={{padding:'8px 12px',textAlign:'left',color:'var(--t3)',fontSize:'9px',borderBottom:'1px solid var(--b0)'}}>{h}</th>)}
                  </tr></thead>
                  <tbody>{compareResults.map((m,i)=>(
                    <tr key={m.name} style={{borderBottom:'1px solid var(--b0)',background:i===0?m.color+'12':'transparent'}}>
                      <td style={{padding:'8px 12px',color:'var(--t3)',fontWeight:700}}>{i===0?'🥇':i===1?'🥈':i===2?'🥉':`#${i+1}`}</td>
                      <td style={{padding:'8px 12px',color:m.color,fontWeight:600}}>{m.name}</td>
                      <td style={{padding:'8px 12px',color:'var(--cyan)',fontFamily:'JetBrains Mono'}}>{m.trainR2}</td>
                      <td style={{padding:'8px 12px',color:m.testR2>0.8?'var(--green)':m.testR2>0.5?'var(--gold)':'var(--red)',fontWeight:700,fontFamily:'JetBrains Mono'}}>{m.testR2}</td>
                      <td style={{padding:'8px 12px',color:m.gap>0.15?'var(--orange)':m.gap>0.05?'var(--gold)':'var(--green)',fontFamily:'JetBrains Mono'}}>{m.gap>0.05?`⚠ ${m.gap}`:`✓ ${m.gap}`}</td>
                      <td style={{padding:'8px 12px',color:'var(--t2)',fontFamily:'JetBrains Mono'}}>{m.testRmse}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </Panel>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
                <Panel title="TEST R² COMPARISON" accent="var(--green)">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={compareResults} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" horizontal={false}/>
                      <XAxis type="number" domain={[0,1]} stroke="var(--t3)" tick={{fontSize:9}}/>
                      <YAxis type="category" dataKey="name" stroke="var(--t3)" tick={{fontSize:9}} width={110}/>
                      <Tooltip formatter={v=>[v,'Test R²']}/>
                      <Bar dataKey="testR2" radius={[0,2,2,0]}>{compareResults.map(m=><Cell key={m.name} fill={m.color} opacity={.85}/>)}</Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Panel>
                <Panel title="TRAIN vs TEST R² (overfit check)" accent="var(--cyan)">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={compareResults}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="name" stroke="var(--t3)" tick={{fontSize:8}} angle={-20} textAnchor="end" height={50}/>
                      <YAxis domain={[0,1]} stroke="var(--t3)" tick={{fontSize:9}}/>
                      <Tooltip/>
                      <Legend/>
                      <Bar dataKey="trainR2" name="Train R²" fill="var(--cyan)" opacity={.7} radius={[2,2,0,0]}/>
                      <Bar dataKey="testR2" name="Test R²" fill="var(--green)" opacity={.85} radius={[2,2,0,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                </Panel>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px',marginTop:'4px'}}>
                {[['OLS','Best for linear relationships, full interpretability. No hyperparameters.','var(--cyan)'],['Ridge/Elastic Net','Best when features are correlated or you need regularization to prevent overfit.','var(--orange)'],['Gradient Boosting','Often top performer on tabular data. Slow to train, little interpretability.','var(--red)'],['Random Forest','Robust ensemble. Handles nonlinearity. Less overfit than single trees.','#34d399']].map(([name,desc,c])=>(
                  <div key={name} style={{padding:'10px',background:'var(--raised)',border:`1px solid ${c}33`,borderRadius:'3px'}}>
                    <div style={{fontSize:'10px',fontWeight:700,color:c,marginBottom:'4px'}}>{name}</div>
                    <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.5}}>{desc}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {!['upload'].includes(tab)&&<LogPanel logs={logs} height={55}/>}
    </div>
  );
}
